import * as React from 'react'
import * as R from 'ramda'
import { graphql, ChildProps } from 'react-apollo'

import {
  ResultDataChildProps,
  JOB_QUERY_SUBSCRIPTION,
  JobQueryResult,
  JOB_QUERY
} from '../ResultData/resultDataQuery'

import { JobResult } from './resultDataQuery'

type RenderType = (
  results: JobResult[] | null,
  numResults: number,
  error?: string
) => React.ReactElement<any> | null // tslint:disable-line

const RESULT_TIMEOUT = 45000

type LoaderProps = {
  render: RenderType
} & ResultDataChildProps

type LoaderState = {
  error?: string
}

/* Use ResultDataLoader when you need access to current results + any incoming results.

   ResultDataLoader sends out a "give mes results for this job id" query and then sets
   up a GraphQL subscription for more results. The raison d'être for this component is
   timeout handling. We don't want to poll for results forever, instead we wait
   RESULT_TIMEOUT seconds and error out if we haven't received any results.

   The render-prop approach used by the component is very similar to React Apollo 2.1
   Subscription component. */
class ResultDataLoader extends React.Component<LoaderProps, LoaderState> {
  unsubscribe: () => void
  timeoutId?: number

  constructor(props: LoaderProps) {
    super(props)
    this.state = { error: undefined }
  }

  componentDidMount() {
    this.setupSubscription(this.props)
  }

  componentWillReceiveProps(nextProps: LoaderProps) {
    if (this.props.jobId === nextProps.jobId) {
      return
    }

    this.setupSubscription(nextProps)
  }

  componentWillUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe()
    }

    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId)
    }
  }

  setupSubscription(props: LoaderProps) {
    if (this.unsubscribe) {
      this.unsubscribe()
    }

    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId)
      this.timeoutId = undefined
    }

    this.setState({ error: undefined })

    if (!props.data) {
      return
    }

    this.unsubscribe = props.data.subscribeToMore({
      document: JOB_QUERY_SUBSCRIPTION,
      variables: {
        jobId: props.jobId
      },
      updateQuery: (previousResult: JobQueryResult, { subscriptionData }) => {
        const data = subscriptionData.data

        // This may actually have an errors object for us, hence the ugly cast.
        if (R.has('errors', subscriptionData)) {
          console.warn('Subscription returned an error', subscriptionData)

          this.setState({
            error:
              'Unfortunately, there was an issue with the processing engine. Please try again.'
          })
          return previousResult
        }

        // For now, we just clear the timer when we get a result from the subscription.
        // In practice, this means that for now, we don't have timeout functionality for
        // subsequent results.
        if (this.timeoutId) {
          window.clearTimeout(this.timeoutId)
          this.timeoutId = undefined
        }

        // We shouldn't *really* need this but let's make sure we don't get duplicate IDs in
        // the store.
        const items = R.unionWith(
          R.eqBy(R.prop('id')),
          previousResult.jobComplete,
          data.jobCompleted
        )

        return {
          ...previousResult,
          jobComplete: items
        }
      },
      onError: err => {
        console.warn(err)
        this.setState({
          error:
            'Unfortunately, there was an issue with the processing engine. Please try again.'
        })
      }
    })

    // Start the timeout timer. This is just for the subscription - it's possible we
    // already have a result (i.e. the graph() wrapped query returned one before we
    // even set up the subscription)
    this.timeoutId = window.setTimeout(() => {
      // If the timeout fired and we still have no results, assume we'll never get any.

      if (
        !this.props.data ||
        (this.props.data.jobComplete &&
          (this.props.data.jobComplete.length === 0 ||
            (this.props.data.jobComplete[0].id === 'no-results' &&
              this.props.data.jobComplete.length === 1)))
      ) {
        this.setState({
          error:
            'Unfortunately, the tool couldn’t find any DELTABEAM® profile with the given input' +
            ', but we can provide you with a personalized solution, please contact Peikko for ' +
            'assistance. (https://www.peikko.com/contact-us/)'
        })

        if (this.unsubscribe) {
          this.unsubscribe()
        }
      }
      this.timeoutId = undefined
    }, RESULT_TIMEOUT)
  }

  render() {
    let results: JobResult[] | null = null
    let error: string | undefined = this.state.error
    let numResults = 3

    if (this.props.data && this.props.data.jobComplete) {
      // Find successful and failed results from the results list (from which we omit the
      // no-results item beforehand)
      const [good, errored] = R.partition(
        res => res.errorMessage === null,
        R.reject(R.propEq('id', 'no-results'), this.props.data.jobComplete)
      )
      results = good

      if (good.length > 0) {
        numResults = good[0].numResults
      }

      // If our state contains an error, it's something fairly generic we set. If the calculation
      // service gives us a more accurate error, use that instead. Also, if we for some reason
      // get an error while we still have "good" results, just ignore the error for now.
      if (good.length === 0 && errored.length > 0) {
        error = errored[0].errorMessage || undefined
      }
    }

    return this.props.render(results, numResults, error)
  }
}
type InputProps = { jobId?: string; render: RenderType }

export default graphql<
  JobQueryResult,
  InputProps,
  ChildProps<InputProps, JobQueryResult>
>(JOB_QUERY, {
  options: props => ({
    variables: {
      jobId: props.jobId
    }
  }),
  skip: ownProps => !ownProps.jobId
})(ResultDataLoader)



// WEBPACK FOOTER //
// ./src/Components/ResultData/index.tsx